/**
 * Easing Utility Functions
 *
 * This module provides easing functions for parametric animation interpolation.
 * Easing functions transform linear progress (0-1) into curved progress for
 * more natural-looking animations.
 *
 * Supported easing types:
 * - linear: Constant speed from start to end
 * - ease-in: Starts slow, speeds up (quadratic)
 * - ease-out: Starts fast, slows down (quadratic)
 * - ease-in-out: Starts slow, speeds up, slows down (quadratic)
 */

import type { EasingFunction } from '../stores/puppetStore';

/**
 * Linear easing - no transformation
 *
 * @param t - Progress value (0-1)
 * @returns Same value unchanged
 */
export function linear(t: number): number {
  return t;
}

/**
 * Ease-in (quadratic) - starts slow, speeds up
 *
 * f(t) = t^2
 *
 * @param t - Progress value (0-1)
 * @returns Eased value (0-1)
 */
export function easeIn(t: number): number {
  return t * t;
}

/**
 * Ease-out (quadratic) - starts fast, slows down
 *
 * f(t) = t * (2 - t) = 2t - t^2
 *
 * This is equivalent to: 1 - (1-t)^2 which is the inverse of ease-in
 *
 * @param t - Progress value (0-1)
 * @returns Eased value (0-1)
 */
export function easeOut(t: number): number {
  return t * (2 - t);
}

/**
 * Ease-in-out (quadratic) - starts slow, speeds up, slows down
 *
 * Piecewise function:
 * - For t < 0.5: 2t^2
 * - For t >= 0.5: -1 + (4 - 2t) * t
 *
 * @param t - Progress value (0-1)
 * @returns Eased value (0-1)
 */
export function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

/**
 * Get eased progress value based on easing function type
 *
 * This is the main entry point for applying easing to animation progress.
 * It clamps the input to 0-1 range before applying the easing function.
 *
 * @param t - Progress value (0-1), clamped if outside range
 * @param easing - Easing function type
 * @returns Eased progress value (0-1)
 *
 * @example
 * ```typescript
 * // Calculate 50% progress with ease-in
 * const easedProgress = getEasedProgress(0.5, 'ease-in');
 * // Result: 0.25 (starts slow)
 *
 * // Calculate 50% progress with ease-out
 * const easedProgress2 = getEasedProgress(0.5, 'ease-out');
 * // Result: 0.75 (ends slow)
 * ```
 */
export function getEasedProgress(t: number, easing: EasingFunction): number {
  // Clamp t to 0-1 range
  const clampedT = Math.max(0, Math.min(1, t));

  switch (easing) {
    case 'linear':
      return linear(clampedT);
    case 'ease-in':
      return easeIn(clampedT);
    case 'ease-out':
      return easeOut(clampedT);
    case 'ease-in-out':
      return easeInOut(clampedT);
    default:
      // Default to linear for unknown easing types
      return linear(clampedT);
  }
}

/**
 * Interpolate between two rotation values
 *
 * Performs linear interpolation (lerp) between two angles.
 * Used after easing has been applied to the progress value.
 *
 * Formula: from + (to - from) * progress
 *
 * @param from - Starting rotation in degrees
 * @param to - Target rotation in degrees
 * @param progress - Interpolation progress (0-1), typically already eased
 * @returns Interpolated rotation value in degrees
 *
 * @example
 * ```typescript
 * // Interpolate from 0 to 90 degrees at 50% progress
 * const rotation = interpolateRotation(0, 90, 0.5);
 * // Result: 45
 *
 * // Can interpolate in negative direction
 * const rotation2 = interpolateRotation(45, -45, 0.5);
 * // Result: 0
 * ```
 */
export function interpolateRotation(
  from: number,
  to: number,
  progress: number
): number {
  return from + (to - from) * progress;
}

/**
 * Calculate interpolated rotation with easing applied
 *
 * Convenience function that combines easing and interpolation in one call.
 * First applies the easing function to the progress, then interpolates.
 *
 * @param from - Starting rotation in degrees
 * @param to - Target rotation in degrees
 * @param t - Raw progress value (0-1)
 * @param easing - Easing function type
 * @returns Interpolated rotation value in degrees with easing applied
 *
 * @example
 * ```typescript
 * // Interpolate from 0 to 90 degrees at 50% progress with ease-in
 * const rotation = interpolateWithEasing(0, 90, 0.5, 'ease-in');
 * // Ease-in at 0.5 = 0.25, so: 0 + (90 - 0) * 0.25 = 22.5
 * ```
 */
export function interpolateWithEasing(
  from: number,
  to: number,
  t: number,
  easing: EasingFunction
): number {
  const easedProgress = getEasedProgress(t, easing);
  return interpolateRotation(from, to, easedProgress);
}
